import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Checkbox,
  Upload,
  message,
  Tag,
  Space,
  Popconfirm,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale, Util } from "../../utils";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const util = Util.getInstance();

function getDefaultFile(fileUrl = "") {
  if (!fileUrl) {
    return [];
  }
  return [
    {
      uid: -1,
      name: `file-${Date.now()}`,
      status: "done",
      url: typeof fileUrl === "boolean" ? "" : fileUrl,
    },
  ];
}

function TransferAccountList({ user }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const refData = useRef(null);

  const fetchList = useCallback(async () => {
    if (!user?.token) {
      return;
    }
    setLoading(true);
    try {
      const res = await api.transferAccount.list.run({ token: user.token });
      if (res.result) {
        setAccounts(Array.isArray(res.data) ? res.data : []);
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    refData.current = null;
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      priority: 10,
      is_default: false,
      purposes: ["commission", "special_share"],
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    refData.current = record;
    form.setFieldsValue({
      label: record.label,
      wechat_merchant_id: record.wechat_merchant_id,
      wechat_merchant_key: record.wechat_merchant_key,
      certificate_serial_no: record.certificate_serial_no,
      v3_key: record.v3_key,
      public_key_id: record.public_key_id,
      daily_quota_yuan: record.daily_quota_yuan,
      priority: record.priority,
      enabled: record.enabled,
      is_default: record.is_default,
      purposes: record.purposes || ["commission", "special_share"],
      public_key: getDefaultFile(record.public_key),
      api_cert: getDefaultFile(record.api_cert),
      api_key: getDefaultFile(record.api_key),
    });
    setModalOpen(true);
  };

  const onUploadFile = useCallback(
    async ({ file, onError, onSuccess }) => {
      if (!file) {
        onError("invalid file");
        return;
      }
      if (!user?.token) {
        onError("no user token");
        return;
      }
      try {
        const res = await api.weapp.upload.run({
          token: user.token,
          uploadFiles: [{ file }],
        });
        if (res.result) {
          onSuccess(res.data);
        } else {
          onError(res.message);
        }
      } catch (error) {
        onError(error);
      }
    },
    [user]
  );

  const uploadField = (fieldName) => ({
    name: fieldName,
    maxCount: 1,
    customRequest: onUploadFile,
  });

  const buildPayload = (values) => {
    const payload = {
      label: values.label,
      wechat_merchant_id: values.wechat_merchant_id,
      wechat_merchant_key: values.wechat_merchant_key,
      certificate_serial_no: values.certificate_serial_no,
      v3_key: values.v3_key,
      public_key_id: values.public_key_id,
      daily_quota_yuan: values.daily_quota_yuan,
      priority: values.priority,
      enabled: values.enabled,
      is_default: values.is_default,
      purposes: values.purposes,
    };

    ["public_key", "api_cert", "api_key"].forEach((field) => {
      const files = values[field];
      if (files?.[0]?.uid !== -1 && files?.[0]?.response) {
        payload[field] = files[0].response[0].path;
      }
    });

    return payload;
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = buildPayload(values);
      const apiConfig = { token: user.token, data: payload };
      let apiUrl = api.transferAccount.create;
      if (editing?.id) {
        apiUrl = api.transferAccount.update;
        apiConfig.path = { id: editing.id };
      }
      const res = await apiUrl.run(apiConfig);
      if (res.result) {
        message.success(res.message || i18n.t("transferAccountList.saved"));
        setModalOpen(false);
        fetchList();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await api.transferAccount.delete.run({
        token: user.token,
        path: { id: record.id },
      });
      if (res.result) {
        message.success(res.message || i18n.t("transferAccountList.deleted"));
        fetchList();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: i18n.t("transferAccountList.label"),
      dataIndex: "label",
      key: "label",
    },
    {
      title: i18n.t("transferAccountList.mchId"),
      dataIndex: "wechat_merchant_id_masked",
      key: "wechat_merchant_id_masked",
    },
    {
      title: i18n.t("transferAccountList.usageToday"),
      key: "usage",
      render: (_, record) => (
        <span>
          ¥{record.usage_today_yuan}
          {record.daily_quota_yuan != null
            ? ` / ¥${record.daily_quota_yuan}`
            : ` (${i18n.t("transferAccountList.unlimited")})`}
        </span>
      ),
    },
    {
      title: i18n.t("transferAccountList.transferCount"),
      dataIndex: "transfer_count_today",
      key: "transfer_count_today",
    },
    {
      title: i18n.t("transferAccountList.purposes"),
      dataIndex: "purposes",
      key: "purposes",
      render: (purposes = []) =>
        purposes.map((item) => (
          <Tag key={item}>{i18n.t(`transferAccountList.purpose_${item}`)}</Tag>
        )),
    },
    {
      title: i18n.t("transferAccountList.priority"),
      dataIndex: "priority",
      key: "priority",
    },
    {
      title: i18n.t("transferAccountList.status"),
      key: "status",
      render: (_, record) => (
        <Space>
          {record.is_default ? <Tag color="blue">{i18n.t("transferAccountList.default")}</Tag> : null}
          <Tag color={record.enabled ? "green" : "default"}>
            {record.enabled
              ? i18n.t("transferAccountList.enabled")
              : i18n.t("transferAccountList.disabled")}
          </Tag>
        </Space>
      ),
    },
    {
      title: i18n.t("transferAccountList.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEdit(record)}>
            {i18n.t("transferAccountList.edit")}
          </Button>
          <Popconfirm
            title={i18n.t("transferAccountList.deleteConfirm")}
            onConfirm={() => onDelete(record)}
          >
            <Button type="link" danger>
              {i18n.t("transferAccountList.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          {i18n.t("transferAccountList.add")}
        </Button>
        <Button onClick={fetchList}>{i18n.t("transferAccountList.refresh")}</Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={accounts}
        pagination={false}
      />

      <Modal
        title={
          editing
            ? i18n.t("transferAccountList.editTitle")
            : i18n.t("transferAccountList.createTitle")
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="label"
            label={i18n.t("transferAccountList.label")}
            rules={[{ required: true, message: i18n.t("transferAccountList.labelRequired") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="wechat_merchant_id"
            label={i18n.t("transferAccountList.mchId")}
            rules={[{ required: true, message: i18n.t("transferAccountList.mchIdRequired") }]}
          >
            <Input disabled={!!editing?.id} />
          </Form.Item>
          <Form.Item name="certificate_serial_no" label={i18n.t("transferAccountList.serialNo")}>
            <Input />
          </Form.Item>
          <Form.Item name="v3_key" label={i18n.t("transferAccountList.v3Key")}>
            <Input />
          </Form.Item>
          <Form.Item name="public_key_id" label={i18n.t("transferAccountList.publicKeyId")}>
            <Input />
          </Form.Item>
          <Form.Item name="wechat_merchant_key" label={i18n.t("transferAccountList.merchantKey")}>
            <Input />
          </Form.Item>
          <Form.Item name="daily_quota_yuan" label={i18n.t("transferAccountList.dailyQuota")}>
            <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder={i18n.t("transferAccountList.unlimitedHint")} />
          </Form.Item>
          <Form.Item name="priority" label={i18n.t("transferAccountList.priority")}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="purposes" label={i18n.t("transferAccountList.purposes")}>
            <Checkbox.Group
              options={[
                { label: i18n.t("transferAccountList.purpose_commission"), value: "commission" },
                { label: i18n.t("transferAccountList.purpose_special_share"), value: "special_share" },
              ]}
            />
          </Form.Item>
          <Form.Item name="enabled" label={i18n.t("transferAccountList.enabled")} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="is_default" label={i18n.t("transferAccountList.default")} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="api_cert" label={i18n.t("transferAccountList.apiCert")} valuePropName="fileList" getValueFromEvent={(e) => util.normFile(e)}>
            <Upload {...uploadField("api_cert")}>
              <Button icon={<UploadOutlined />}>{i18n.t("transferAccountList.upload")}</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="api_key" label={i18n.t("transferAccountList.apiKey")} valuePropName="fileList" getValueFromEvent={(e) => util.normFile(e)}>
            <Upload {...uploadField("api_key")}>
              <Button icon={<UploadOutlined />}>{i18n.t("transferAccountList.upload")}</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="public_key" label={i18n.t("transferAccountList.publicKey")} valuePropName="fileList" getValueFromEvent={(e) => util.normFile(e)}>
            <Upload {...uploadField("public_key")}>
              <Button icon={<UploadOutlined />}>{i18n.t("transferAccountList.upload")}</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default connect((state) => ({ user: getUserInfo(state) }))(TransferAccountList);
